import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Storefront, Plus, LinkSimple, Copy, ArrowRight, CheckCircle,
} from "@phosphor-icons/react";

// Reusable Landing Page System · Phase 2 — Campaign detail "Landing Pages" tab.
// Lists landing pages linked to this campaign (via lp.campaign_id) and offers
// a one-tap create with campaign_id pre-filled.

export default function CampaignLandingPages({ campaignId, campaignName }) {
  const [items, setItems] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ name: "", form_id: "", headline: "" });

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [lp, f] = await Promise.all([
        api.get("/landing-pages", { params: { campaign_id: campaignId } }),
        api.get("/forms").catch(() => ({ data: [] })),
      ]);
      setItems(lp.data || []);
      setForms(f.data || []);
    } catch {
      setItems([]); setForms([]);
    } finally { setLoading(false); }
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = draft.name.trim();
    const headline = draft.headline.trim();
    if (!name) { toast.error("Name is required"); return; }
    if (!draft.form_id) { toast.error("Pick a form"); return; }
    if (!headline) { toast.error("Headline is required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/landing-pages", {
        name, form_id: draft.form_id, headline,
        campaign_id: campaignId,
      });
      setItems((rs) => [data, ...rs]);
      setShowForm(false);
      setDraft({ name: "", form_id: "", headline: "" });
      toast.success("Landing page created for this campaign");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const publicUrlFor = (slug) => `${window.location.origin}/p/${slug}`;
  const copyUrl = async (slug) => {
    try { await navigator.clipboard.writeText(publicUrlFor(slug)); toast.success("Public URL copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-4" data-testid="campaign-landing-pages">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
            <Storefront weight="duotone" className="h-3 w-3" /> Landing pages for this campaign
          </div>
          <p className="mt-2 text-sm text-ink-secondary max-w-xl">
            Pages explicitly linked to <span className="text-ink-primary">{campaignName || "this campaign"}</span> via campaign_id.
            The linked form's attribution still applies for lead capture — this direct link makes finding & filtering easier.
          </p>
        </div>
        {!showForm && (
          <button
            data-testid="campaign-lp-create-button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200"
          >
            <Plus weight="bold" className="h-4 w-4" /> Create landing page for this campaign
          </button>
        )}
      </div>

      {showForm && (
        <section className="rounded-md border border-white/10 bg-graphite-900 p-5 space-y-3"
                  data-testid="campaign-lp-create-form">
          <div className="text-[11px] uppercase tracking-widest text-ink-secondary flex items-center gap-2">
            <CheckCircle weight="duotone" className="h-3 w-3 text-emerald-300" />
            campaign_id pre-filled · {campaignId.slice(0, 12)}…
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Internal name</label>
            <input
              data-testid="campaign-lp-form-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              maxLength={200}
              className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
              placeholder="e.g. Q4 Launch Landing"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Form to embed</label>
            <select
              data-testid="campaign-lp-form-formid"
              value={draft.form_id}
              onChange={(e) => setDraft({ ...draft, form_id: e.target.value })}
              className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
            >
              <option value="">— Pick a form —</option>
              {forms.map(f => <option key={f.form_id} value={f.form_id}>{f.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Hero headline</label>
            <input
              data-testid="campaign-lp-form-headline"
              value={draft.headline}
              onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
              maxLength={200}
              className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
              placeholder="Turn dormant leads into demos in 14 days"
            />
            <p className="text-[11px] text-ink-secondary/70">You can add more sections (problem, solution, benefits, proof, etc.) from the Landing Pages page after creation.</p>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="campaign-lp-form-save"
              onClick={create}
              disabled={creating}
              className="rounded-full bg-white text-graphite-950 px-4 py-1.5 text-xs font-medium hover:bg-neutral-100 disabled:opacity-60"
            >
              {creating ? "Creating…" : "Create landing page"}
            </button>
            <button
              onClick={() => { setShowForm(false); setDraft({ name: "", form_id: "", headline: "" }); }}
              disabled={creating}
              className="rounded-full border border-white/10 px-4 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5"
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {loading ? (
        <div className="text-sm text-ink-secondary" data-testid="campaign-lp-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-white/10 bg-graphite-950/40 p-6 text-center"
              data-testid="campaign-lp-empty">
          <Storefront weight="duotone" className="mx-auto h-8 w-8 text-ink-secondary/60" />
          <div className="mt-3 text-sm text-ink-secondary">No landing pages linked to this campaign yet.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="campaign-lp-list">
          {items.map((p) => (
            <li
              key={p.landing_page_id}
              data-testid={`campaign-lp-item-${p.landing_page_id}`}
              className="rounded-md border border-white/10 bg-graphite-900 p-4 flex items-center gap-4 flex-wrap"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ink-primary">{p.name}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <LinkSimple weight="bold" className="h-3 w-3" />
                    /p/{p.public_slug}
                    <button onClick={() => copyUrl(p.public_slug)} className="text-ink-secondary hover:text-ink-primary"
                            data-testid={`campaign-lp-copy-${p.landing_page_id}`}>
                      <Copy weight="bold" className="h-3 w-3" />
                    </button>
                  </span>
                  <span>{(p.sections || []).filter(s => s.enabled).length} of {(p.sections || []).length} sections</span>
                  {p.form_name && <span>form · {p.form_name}</span>}
                  {p.is_active
                    ? <span className="text-emerald-200">active</span>
                    : <span className="text-ink-secondary/60">inactive</span>}
                </div>
              </div>
              <Link
                to="/landing-pages"
                data-testid={`campaign-lp-open-${p.landing_page_id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-graphite-950 px-3 py-1.5 text-xs text-ink-primary hover:bg-graphite-800"
              >
                Manage <ArrowRight weight="bold" className="h-3 w-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
